import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import PrivacyPolicyPDF from '../../assets/documents/Shipmnts-Privacy-Policy.pdf';

const PrivacyPolicy = () => {
  const EFFECTIVE_DATE = '10th Feb, 2026';
  const LAST_UPDATED = '10th Feb, 2026';
  const SUPPORT_EMAIL = 'privacy@shipmnts.com';
  
  const handleDownloadPDF = () => {
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = PrivacyPolicyPDF;
    link.download = 'Shipmnts-Privacy-Policy.pdf'; // Suggested filename for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="privacy-policy-container first-section">
      <Helmet>
        <title>Privacy Policy | Shipmnts</title>
        <meta name="description" content="Privacy Policy for Shipmnts.com - Learn how we collect, use, and protect your data." />
      </Helmet>

      <div className="privacy-policy-content">
        <h1 className="privacy-title">Privacy Policy</h1>

        <div className="effective-dates">
          <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
          <p><strong>Last Updated:</strong> {LAST_UPDATED}</p>
        </div>

        <p className="intro-text">
          Logistixian Technologies Private Limited ("Logistixian", "we", "us", "our") values the privacy of individuals and organisations that access and use our SaaS platform Shipmnts.com ("Platform", "Services"). This Privacy Policy explains how we collect, use, disclose and safeguard your personal and business data in connection with your use of our Platform and related services.
        </p>

        <p className="consent-notice">
          By accessing or using Shipmnts.com or our Services, you consent to the terms of this Privacy Policy. If you do not agree with any part of this policy, please do not use our Services.
        </p>

        {/* Section 1 */}
        <div className="policy-section">
          <h2>1. Scope & Applicability</h2>
          <p>This Privacy Policy applies to:</p>
          <ul>
            <li>Individuals ("you", "your") who access or interact with our Platform, website, support channels, or communications;</li>
            <li>Customers and their users (e.g., employees, agents) who use the Platform under a subscription or contractual arrangement;</li>
            <li>Visitors to our websites and marketing pages.</li>
          </ul>
        </div>

        {/* Section 2 */}
        <div className="policy-section">
          <h2>2. What Information We Collect</h2>
          <p>We may collect the following categories of data:</p>
          <ul>
            <li>
              <strong>Personal & Contact Information</strong> – Name, email address, telephone number, company name, job title, billing address, billing contact details
            </li>
            <li>
              <strong>Account & Usage Data</strong> – Login credentials (securely hashed), user roles and preferences, platform usage activity, support tickets
            </li>
            <li>
              <strong>Technical & Device Information</strong> – IP address, device type, browser type and version, operating system, device identifiers, cookies and similar tracking technologies
            </li>
            <li>
              <strong>Payment & Billing Data</strong> – Payment identifiers and transaction details, billing history processed via third-party payment providers (we do not store raw credit card or netbanking credentials)
            </li>
            <li>
              <strong>Communications Data</strong> – Messages exchanged with support or sales, marketing preferences
            </li>
            <li>
              <strong>Aggregated & Derived Data</strong> – Anonymous analytics, trends and performance data that cannot be used to identify you
            </li>
          </ul>
          <p className="note">We do not collect special categories of sensitive personal data (e.g., health, race, religion).</p>
        </div>

        {/* Section 3 */}
        <div className="policy-section">
          <h2>3. How We Collect Information</h2>
          <p>Information may be collected through:</p>
          <ul>
            <li>Direct interactions (account creation, support requests)</li>
            <li>Automated technologies (cookies, web beacons, server logs)</li>
            <li>Third-party services (analytics providers, advertising networks)</li>
          </ul>
          <p>Some data is collected automatically when you use the Platform. You can manage cookie preferences through your browser settings, though disabling cookies may affect functionality.</p>
        </div>

        {/* Section 4 */}
        <div className="policy-section">
          <h2>4. How We Use Your Data</h2>
          <p>We may use your information for the following purposes:</p>
          <ul>
            <li>
              <strong>Service Delivery</strong> – To provide, maintain, and improve the Platform; to process subscriptions, invoices, and payments
            </li>
            <li>
              <strong>Customer Support & Engagement</strong> – To respond to inquiries and support requests; to provide service notifications and alerts
            </li>
            <li>
              <strong>Analytics & Improvement</strong> – To analyse usage trends and enhance features; to conduct research and diagnostics
            </li>
            <li>
              <strong>Marketing & Communications</strong> – To send newsletters, offers, and product updates (with your consent where required)
            </li>
            <li>
              <strong>Security & Compliance</strong> – To detect and prevent fraud; to meet legal, regulatory, contractual, and compliance obligations
            </li>
            <li>
              <strong>Legal Defense</strong> – To enforce our Terms, protect our rights, and manage disputes
            </li>
          </ul>
          <p>We will only use your data for purposes compatible with the original collection purposes, unless otherwise permitted by law.</p>
        </div>

        {/* Section 5 */}
        <div className="policy-section">
          <h2>5. Sharing Your Information</h2>
          <p className="highlight">We do not sell, rent, or trade your personal information.</p>
          <p>We may share data with:</p>
          <ul>
            <li>Service Providers (cloud hosting, analytics, email delivery)</li>
            <li>Payment Processors (PCI-DSS compliant partners)</li>
            <li>Regulators or legal authorities when required by law</li>
            <li>Professional advisors (legal, accounting, auditors)</li>
            <li>Affiliates and subcontractors under confidentiality obligations</li>
          </ul>
          <p>All third parties must process your data in accordance with contractual safeguards.</p>
        </div>

        {/* Section 6 */}
        <div className="policy-section">
          <h2>6. Cross-Border Data Transfers</h2>
          <p>Your data may be transferred across countries (including India, United States, Singapore, EU, Middle East). We implement appropriate safeguards and may rely on applicable legal mechanisms (e.g., standard contractual clauses, consent) to support lawful transfers.</p>
        </div>

        {/* Section 7 */}
        <div className="policy-section">
          <h2>7. Data Security</h2>
          <p>We implement industry-standard technical and organizational measures:</p>
          <ul>
            <li>Encryption at rest and in transit</li>
            <li>Secure development practices</li>
            <li>Role-based access controls</li>
            <li>Network and application-level monitoring</li>
          </ul>
          <p>No system is completely secure; however, we strive to protect your data from unauthorized access.</p>
        </div>

        {/* Section 8 */}
        <div className="policy-section">
          <h2>8. Data Retention</h2>
          <p>We retain information only as long as necessary to:</p>
          <ul>
            <li>Provide the Services</li>
            <li>Comply with legal retention requirements</li>
            <li>Protect against disputes</li>
          </ul>
          <p>After retention periods expire, data is either deleted or anonymised.</p>
        </div>

        {/* Section 9 */}
        <div className="policy-section">
          <h2>9. Your Rights (Jurisdiction-Specific)</h2>
          <p>Depending on your location and applicable law, you may have rights including:</p>
          <ul>
            <li>Access, correction, deletion</li>
            <li>Objecting to processing</li>
            <li>Restriction of processing</li>
            <li>Data portability</li>
            <li>Withdrawal of consent</li>
          </ul>
          <p>For California residents, additional rights under CCPA/CPRA apply. For EU/EEA users, GDPR rights apply. To exercise your rights, contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
        </div>

        {/* Section 10 */}
        <div className="policy-section">
          <h2>10. Cookies & Tracking</h2>
          <p>We use cookies and similar tools to:</p>
          <ul>
            <li>Enable essential site functionality</li>
            <li>Personalise your experience</li>
            <li>Perform analytics & improve offerings</li>
          </ul>
          <p>A separate Cookie Policy provides detailed information.</p>
        </div>

        {/* Section 11 */}
        <div className="policy-section">
          <h2>11. Children's Privacy</h2>
          <p>Our Platform is intended for business users and adults. We do not knowingly collect personal data from children under the age of 16. If we learn that we have obtained such data without consent, we will take steps to delete it.</p>
        </div>

        {/* Section 12 */}
        <div className="policy-section">
          <h2>12. Changes to this Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you via the Platform or by email if there are material changes. Continued use of our Services after updates implies acceptance.</p>
        </div>

        {/* Section 13 */}
        <div className="policy-section contact-section">
          <h2>13. Contact Information</h2>
          <p>If you have questions or requests related to this Privacy Policy, please contact:</p>
          <div className="contact-card">
            <p className="company-name">Logistixian Technologies Private Limited</p>
            <p>
              <strong>Address:</strong><br />
              A-28, Pariseema Complex,<br />
              Chimanlal Girdharlal Rd, Ellisbridge,<br />
              Ahmedabad, Gujarat 380006
            </p>
            <p><strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
          </div>
        </div>

        {/* Related Links */}
        <div className="policy-section related-links no-print">
          <h2>Related Policies</h2>
          <div className="links-container">
            <Link to="/cookie-policy" className="policy-link">
              Cookie Policy
            </Link>
            <Link to="/terms-of-services" className="policy-link">
              Terms of Service
            </Link>
          </div>
        </div>

        {/* Download PDF Button */}
        <div className="download-section no-print">
          <button 
            className="download-pdf-btn"
            onClick={handleDownloadPDF}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download as PDF
          </button>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
